import { useState, useRef, useEffect, useCallback } from 'react'
import api from '../services/api'

interface Servico {
  id:                      number
  descricao:               string
  cod_tributacao_nacional:  string
  cod_tributacao_municipal: string
  nbs:                     string
  aliquota_iss:            string
  piscofins_cst:           string
  perc_inss:               string
}

interface Props {
  value: number | null
  descricao: string
  onChange: (s: Servico | null) => void
}

export default function ServicoNFSeSelect({ value, descricao, onChange }: Props) {
  const [aberta, setAberta]       = useState(false)
  const [filtro, setFiltro]       = useState('')
  const [resultados, setResultados] = useState<Servico[]>([])
  const [buscando, setBuscando]   = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setAberta(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const buscar = useCallback((valor: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (valor.length < 1) { setResultados([]); return }
    debounceRef.current = setTimeout(async () => {
      setBuscando(true)
      try {
        const r = await api.get('/nfse-servicos', { params: { busca: valor } })
        setResultados(r.data)
      } finally {
        setBuscando(false)
      }
    }, 350)
  }, [])

  async function carregarTodos() {
    setBuscando(true)
    try {
      const r = await api.get('/nfse-servicos/todos')
      setResultados(r.data)
    } finally {
      setBuscando(false)
    }
  }

  function selecionar(s: Servico) {
    onChange(s)
    setAberta(false)
    setFiltro('')
    setResultados([])
  }

  function limpar() {
    onChange(null)
    setAberta(false)
    setFiltro('')
    setResultados([])
  }

  return (
    <div className="nat-wrap" ref={ref}>
      <div className="nat-input-row">
        <div
          className="nat-desc-box"
          tabIndex={0}
          style={{ flex: 1 }}
          onClick={() => { const abrindo = !aberta; setAberta(abrindo); setFiltro(''); setResultados([]); if (abrindo) carregarTodos() }}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAberta(o => !o) } }}
        >
          {descricao
            ? <span className="nat-dsc">{descricao}</span>
            : <span className="nat-placeholder">Selecione o serviço...</span>
          }
          <span className="nat-chevron">{aberta ? '▴' : '▾'}</span>
        </div>
        {value !== null && (
          <button
            type="button"
            onClick={limpar}
            style={{ padding: '0 10px', background: 'none', border: '1px solid #e5e7eb', borderRadius: 4, cursor: 'pointer', color: '#6b7280' }}
            title="Limpar"
          >✕</button>
        )}
      </div>

      {aberta && (
        <div className="nat-lista">
          <div className="nat-filtro-wrap">
            <input
              autoFocus
              className="nat-filtro"
              placeholder="Buscar por descrição ou cód. tributação..."
              value={filtro}
              onChange={e => { setFiltro(e.target.value); buscar(e.target.value) }}
            />
          </div>
          <div className="nat-itens">
            {buscando && <div className="nat-vazio">Buscando...</div>}
            {!buscando && resultados.length === 0 && (
              <div className="nat-vazio">Nenhum serviço cadastrado</div>
            )}
            {resultados.map(s => (
              <button
                key={s.id}
                className={`nat-item${s.id === value ? ' nat-item--ativo' : ''}`}
                onClick={() => selecionar(s)}
              >
                <div className="nat-item-linha1">
                  <span className="nat-item-cfop">Trib. Nacional: {s.cod_tributacao_nacional}</span>
                  {s.nbs && <span style={{ fontSize: '0.72rem', color: '#6b7280' }}>NBS {s.nbs}</span>}
                  <span style={{ fontSize: '0.72rem', color: '#6b7280' }}>ISS {s.aliquota_iss}%</span>
                </div>
                <div className="nat-item-dsc">{s.descricao}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
